/**
 * Utility functions for parsing and handling club member responses
 */

/**
 * Parse member response from club_members endpoint
 * Handles different response formats from the backend
 * @param {*} response - API response from club_members endpoint
 * @returns {Array} Array of member objects
 */
export const parseMemberResponse = (response) => {
  // Handle null/undefined
  if (!response) {
    return [];
  }
  
  // If it's already an array, return it (legacy format)
  if (Array.isArray(response)) {
    return response;
  }
  
  // If it's an object, check for members array
  if (typeof response === 'object') {
    // Check for direct members array (most common case - new endpoint format)
    if (Array.isArray(response.members)) {
      return response.members;
    }
    
    // Check for nested data.members
    if (response.data && Array.isArray(response.data.members)) {
      return response.data.members;
    }
    
    // Check for nested data array
    if (Array.isArray(response.data)) {
      return response.data;
    }
    
    // Check for connections array (fallback for backwards compatibility)
    if (Array.isArray(response.connections)) {
      return response.connections;
    }
  }
  
  return [];
};

/**
 * Extract pagination metadata from member response
 * @param {*} response - API response from club_members endpoint
 * @returns {Object} Pagination metadata { total, has_more, offset, limit }
 */
export const extractMemberPagination = (response) => {
  if (!response || typeof response !== 'object') {
    return {
      total: 0,
      has_more: false,
      offset: 0,
      limit: 0
    };
  }
  
  return {
    total: response.total || 0,
    has_more: response.has_more || false,
    offset: response.offset || 0,
    limit: response.limit || 0
  };
};

/**
 * Extract member ID from a member object
 * Handles different ID field names that may be present
 * @param {Object} member - Member object
 * @returns {string|number|null} Member ID or null if not found
 */
export const getMemberId = (member) => {
  if (!member || typeof member !== 'object') {
    return null;
  }
  
  // Try different possible ID field names
  return member.user_id || member.runner_id || member.id || null;
};

/**
 * Format member join date for display
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string|null} Formatted date string or null if invalid
 */
export const formatMemberJoinDate = (dateString) => {
  if (!dateString) {
    return null;
  }
  
  try {
    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return null;
    }
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting member join date:', error);
    return null;
  }
};
